import type { BaseEntity } from './BaseEntity';

/**
 * F 275 — THE PROJECT RECORD. The floor of the FVC boundary.
 *
 * WHY THIS FILE EXISTS. FVC 745 audited every project store reachable in the
 * fleet and found the same conclusion from five directions: a browser-local
 * key duplicated across two maps, a directory of files written by the chat
 * bridge, and a one-row PocketBase deploy registry. "The fleet project concept
 * is a browser-local key, a filesystem path and a one-row deploy table: three
 * unrelated mechanisms sharing a word."
 *
 * `'project'` has been a member of `EntityKind` since FVC 170, and the
 * classifier has recognised a `_project.json` sentinel since FVC 50. What never
 * existed is a record type behind the name — nothing to construct, nothing to
 * persist, nothing a host could hand a project to. **The kind was a name with
 * no path.** This is the path.
 *
 * WHY THE SHAPE IS THIS SMALL. The operator chose Shape A: project first, `app`
 * and `view` later, once the floor is proven. That is the right order, because
 * `app` and `view` are the same record with the same two meaningful fields
 * (an id and a name) one tier lower. If the floor is wrong, both of them are
 * wrong twice. If it is right, they are copies of ~40 lines each.
 *
 * WHY IT EXTENDS `BaseEntity`. It is a node in the containment tree like every
 * other entity: it has an `id`, an `fcid`, `parentId`, `order`, and the F 226
 * `deletedAt` that makes Trash work. A project that opted out of the tree would
 * be a second identity system in the substrate, which is the exact failure FVC
 * 745 documented.
 *
 * FVC 740's planned `projectId` on every tree node is what points HERE. That
 * field lives in VC's `ExtractedNode`, not in this record: the F 275 scope is
 * the store, and the pointer is FVC 740's dispatch. This record is what such a
 * pointer will resolve to.
 */

export const PROJECT_FCID_PREFIX = 'fcid://project/';

/** Stable fcid for a project id. The canonical reference, as for every kind. */
export function projectFcid(id: string): string {
  return `${PROJECT_FCID_PREFIX}${id}`;
}

/**
 * A project is the durable identity behind a unit of work.
 *
 * It is NOT the VC "project" of `WorkspaceManager.activeProjectId` by
 * construction — nothing here imports VC. It is the substrate's own record, and
 * the two converge only where a host chooses to write one from the other.
 */
/**
 * F 282 — a capability a project DECLARES it holds.
 *
 * The fleet-scope rule (F 273 decision two, implemented here) is that a project
 * reads across projects only if it holds this. It is declared, not inferred
 * from role: a blog that genuinely needs to read its sibling is one array entry
 * away, and an audit can see who has it.
 *
 * 'fleet-read' is the only value in v1. It is a string union rather than a
 * boolean so a second capability cannot be added by widening a flag.
 */
export type ProjectCapability = 'fleet-read';

export interface Project extends BaseEntity {
  /**
   * Capabilities this project holds. Absent or empty means it holds none.
   *
   * HQ holds 'fleet-read' by default because HQ is the fleet aggregate and
   * reading every lane is its job. A lane-project does NOT hold it by default —
   * absence is the safe default, not presence.
   */
  capabilities?: ProjectCapability[];

  /** Canonical reference. Present on every kind; declared here as Setting does. */
  fcid: string;
  /** The project's name. The only field a human is required to supply. */
  name: string;
  /**
   * Optional human-facing label, when `name` is a slug or a path and the
   * display text differs. Mirrors `Setting.label`: `BaseEntity` has no `label`,
   * so the field is declared rather than assumed.
   */
  label?: string;
  /**
   * The lane this project belongs to, as a string id.
   *
   * STRING, NOT A NUMBER, and the reason matters: `id` on `BaseEntity` is a
   * string, `fcid` segments are `[a-z][a-z0-9-]*` (FCID_RE in
   * src/core/refs/resolve.ts), and HQ's `thread_id` is an integer column that
   * this substrate must be able to name without importing a database type.
   * A number here would need a string cast at every one of those boundaries,
   * and the cast is where a lane id silently becomes `"12"` and then `"12 "`.
   *
   * NULL means the fleet project itself, or any project that predates lanes.
   * That is the same nullable-field-means-the-exception move F 156 made for
   * `parentId` and F 223 for `edge`: absent carries a meaning, so it cannot be
   * required without invalidating rows that say nothing.
   */
  lane?: string | null;
  /** Free-form description. Never parsed. */
  description?: string;
  /** Default branch or ref, when the project is a code project. Optional. */
  defaultRef?: string;
  /** Repository slug, when the project is a code project. Optional. */
  repo?: string;
}

/** True when `value` is a project record. Proves the kind survives a round-trip. */
export function isProject(value: unknown): value is Project {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as Project).fcid === 'string' &&
    (value as Project).fcid.startsWith(PROJECT_FCID_PREFIX) &&
    typeof (value as Project).name === 'string'
  );
}

export interface CreateProjectInput {
  id?: string;
  name: string;
  label?: string;
  lane?: string | null;
  description?: string;
  defaultRef?: string;
  repo?: string;
  parentId?: string | null;
  order?: number | null;
  now?: string;
}

/**
 * Construct a project.
 *
 * Fails loudly on an empty name rather than storing a nameless row: a project
 * you cannot name cannot be selected in a list, and a list of blank rows is
 * the exact shape of the FVC 745 finding.
 */
export function createProject(input: CreateProjectInput): Project {
  const name = typeof input.name === 'string' ? input.name.trim() : '';
  if (!name) throw new Error('createProject: name is required and must be non-empty');
  const now = input.now ?? new Date().toISOString();
  const id = input.id?.trim() || `proj-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;
  if (!id) throw new Error('createProject: id could not be derived from name');
  return {
    id,
    fcid: projectFcid(id),
    name,
    label: input.label,
    lane: input.lane ?? null,
    description: input.description,
    defaultRef: input.defaultRef,
    repo: input.repo,
    parentId: input.parentId ?? null,
    order: input.order ?? null,
    createdAt: now,
    updatedAt: now,
  };
}
